import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import TodayIcon from "@mui/icons-material/TodayOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonthOutlined";
import PeopleIcon from "@mui/icons-material/PeopleAltOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined";
import QueryStatsIcon from "@mui/icons-material/QueryStatsOutlined";
import Inventory2Icon from "@mui/icons-material/Inventory2Outlined";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcardOutlined";
import LocalOfferIcon from "@mui/icons-material/LocalOfferOutlined";
import CampaignIcon from "@mui/icons-material/CampaignOutlined";
import NotificationsIcon from "@mui/icons-material/NotificationsNoneOutlined";
import MedicalServicesIcon from "@mui/icons-material/MedicalServicesOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import TuneIcon from "@mui/icons-material/TuneOutlined";
import RoomServiceIcon from "@mui/icons-material/RoomServiceOutlined";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import logo from "../assets/softwarezz-icon.png";
import { PageIconContext } from "../components/PageHeader";
import PageTransition from "../components/PageTransition";
import SubscriptionBanner from "../components/subscription/SubscriptionBanner";
import TourProvider from "../components/tour/TourProvider";
import HelpRow from "../components/tour/HelpRow";
import { useFeatures } from "../contexts/FeaturesContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import { useUser } from "../contexts/UserContext";
import { logout } from "../services/authService";
import { ClientSelectItem, getClientsSelect } from "../services/ClientsService";
import { getAllBusinesses } from "../services/VenueService";
import { BRAND, BUSINESS_THEME, BUSINESS_TYPE, LABELS } from "../platform/config";

/* Tokens in the business type's colours, kept local so the sports layout is untouched. */
const C = {
  ink: BUSINESS_THEME.text,
  muted: BUSINESS_THEME.textSecondary,
  line: BUSINESS_THEME.border,
  page: BUSINESS_THEME.background,
  teal: BUSINESS_THEME.main,
  tealTint: BUSINESS_THEME.tint,
};

const NAV_WIDTH = 252;

interface NavLeaf {
  label: string;
  icon: React.ReactNode;
  to: string;
  /** Exact match only (the Today page is the index route). */
  exact?: boolean;
  feature?: string;
}

interface NavGroup {
  label: string;
  icon: React.ReactNode;
  feature?: string;
  children: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

interface NavSection {
  title?: string;
  entries: NavEntry[];
}

const isGroup = (entry: NavEntry): entry is NavGroup =>
  (entry as NavGroup).children !== undefined;

/**
 * The clinic backoffice shell. Deliberately not the sports sidebar: the day
 * starts on "Today", the three things a front desk lives in (Today, Schedule,
 * Patients) sit on top, and the business side is grouped below. Phones get a
 * bottom tab bar, a doctor checking the next patient between consultations
 * shouldn't hunt through a drawer.
 */
const ClinicLayout: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { businessId } = useParams<{ businessId: string }>();
  const base = `/dashboard/${businessId}`;
  const { user } = useUser();
  const { isEnabled, showFeatureLocked } = useFeatures();
  const { subscription } = useSubscription();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [venues, setVenues] = useState<any[]>([]);
  const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!businessId) navigate("/home", { replace: true });
  }, [businessId, navigate]);

  useEffect(() => {
    getAllBusinesses()
      .then((list) => setVenues(Array.isArray(list) ? list : []))
      .catch(() => setVenues([]));
  }, []);

  const clinicName =
    venues.find((v) => String(v.id) === String(businessId))?.name || BRAND.name;

  const sections: NavSection[] = useMemo(
    () => [
      {
        entries: [
          { label: "Today", icon: <TodayIcon />, to: base, exact: true },
          { label: "Schedule", icon: <CalendarMonthIcon />, to: `${base}/appointment` },
          { label: LABELS.clients, icon: <PeopleIcon />, to: `${base}/clients/clients` },
        ],
      },
      {
        title: "Business",
        entries: [
          {
            label: "Finance",
            icon: <ReceiptIcon />,
            children: [
              { label: "Invoices", icon: <ReceiptIcon />, to: `${base}/finance/invoices` },
              { label: "Payments", icon: <ReceiptIcon />, to: `${base}/reports/payments` },
              { label: "Expenses", icon: <ReceiptIcon />, to: `${base}/finance/expenses` },
              { label: "Refunds", icon: <ReceiptIcon />, to: `${base}/finance/refunds` },
              { label: "Revenue", icon: <ReceiptIcon />, to: `${base}/reports/revenue` },
              { label: "Profit & loss", icon: <ReceiptIcon />, to: `${base}/reports/pnl` },
              { label: "Reports", icon: <ReceiptIcon />, to: `${base}/finance/reports` },
            ],
          },
          { label: "Bookings overview", icon: <QueryStatsIcon />, to: `${base}/overview` },
          {
            label: "Supplies",
            icon: <Inventory2Icon />,
            feature: "STOCK",
            children: [
              { label: "Stock", icon: <Inventory2Icon />, to: `${base}/stock` },
              { label: "Purchases", icon: <Inventory2Icon />, to: `${base}/purchases` },
              { label: "Products", icon: <Inventory2Icon />, to: `${base}/products` },
            ],
          },
        ],
      },
      {
        title: "Engage",
        entries: [
          {
            label: "Loyalty",
            icon: <CardGiftcardIcon />,
            feature: "LOYALTY",
            children: [
              { label: "Settings", icon: <CardGiftcardIcon />, to: `${base}/loyalty/settings` },
              { label: `${LABELS.client} points`, icon: <CardGiftcardIcon />, to: `${base}/loyalty/clients` },
            ],
          },
          { label: "Promo codes", icon: <LocalOfferIcon />, to: `${base}/promocode` },
          { label: "Notifications", icon: <NotificationsIcon />, to: `${base}/userNotifications` },
          { label: "Banners", icon: <CampaignIcon />, to: `${base}/banners` },
        ],
      },
      {
        title: `${LABELS.venue} setup`,
        entries: [
          {
            label: "Services",
            icon: BUSINESS_TYPE === "CLINIC" ? <MedicalServicesIcon /> : <RoomServiceIcon />,
            to: `${base}/settings/services`,
          },
          { label: `${LABELS.clientFile} settings`, icon: <DrawOutlinedIcon />, to: `${base}/settings/consent-forms` },
          { label: "Settings", icon: <TuneIcon />, to: `${base}/settings/advanced` },
          {
            label: "Notification center",
            icon: <NotificationsActiveOutlinedIcon />,
            to: `${base}/settings/notification-center`,
          },
          { label: "Business type", icon: <StorefrontIcon />, to: `${base}/settings/business-type` },
          { label: "Subscription", icon: <WorkspacePremiumIcon />, to: `${base}/subscription` },
        ],
      },
    ],
    [base]
  );

  const isActive = (leaf: NavLeaf) =>
    leaf.exact
      ? location.pathname.replace(/\/$/, "") === leaf.to
      : location.pathname.startsWith(leaf.to);

  // The active page's icon and title, for PageHeader and the top bar.
  const activeLeaf = useMemo(() => {
    for (const section of sections) {
      for (const entry of section.entries) {
        const leaves = isGroup(entry) ? entry.children : [entry];
        const hit = leaves.find((leaf) => isActive(leaf));
        if (hit) return hit;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, location.pathname]);

  const go = (leaf: NavLeaf, feature?: string) => {
    const gate = leaf.feature || feature;
    if (gate && !isEnabled(gate)) {
      showFeatureLocked(gate);
      return;
    }
    setDrawerOpen(false);
    navigate(leaf.to);
  };

  const renderLeaf = (leaf: NavLeaf, nested = false, feature?: string) => {
    const active = isActive(leaf);
    const locked = !!(leaf.feature || feature) && !isEnabled((leaf.feature || feature)!);
    return (
      <ListItemButton
        key={leaf.to}
        selected={active}
        onClick={() => go(leaf, feature)}
        sx={{
          borderRadius: 2.5,
          minHeight: 44,
          mb: 0.25,
          pl: nested ? 6.5 : 1.5,
          color: active ? C.teal : C.ink,
          "&.Mui-selected": { bgcolor: C.tealTint, "&:hover": { bgcolor: C.tealTint } },
        }}
      >
        {!nested && (
          <ListItemIcon sx={{ minWidth: 38, color: active ? C.teal : C.muted }}>
            {leaf.icon}
          </ListItemIcon>
        )}
        <ListItemText
          primary={leaf.label}
          primaryTypographyProps={{
            fontSize: nested ? "0.88rem" : "0.94rem",
            fontWeight: active ? 700 : 500,
          }}
        />
        {locked && <LockOutlinedIcon sx={{ fontSize: 16, color: C.muted }} />}
      </ListItemButton>
    );
  };

  const renderEntry = (entry: NavEntry) => {
    if (!isGroup(entry)) return renderLeaf(entry);
    const childActive = entry.children.some((leaf) => isActive(leaf));
    const open = openGroups[entry.label] ?? childActive;
    const locked = !!entry.feature && !isEnabled(entry.feature);
    return (
      <Box key={entry.label}>
        <ListItemButton
          onClick={() => {
            if (locked && entry.feature) {
              showFeatureLocked(entry.feature);
              return;
            }
            setOpenGroups((prev) => ({ ...prev, [entry.label]: !open }));
          }}
          sx={{ borderRadius: 2.5, minHeight: 44, mb: 0.25, pl: 1.5, color: C.ink }}
        >
          <ListItemIcon sx={{ minWidth: 38, color: childActive ? C.teal : C.muted }}>
            {entry.icon}
          </ListItemIcon>
          <ListItemText
            primary={entry.label}
            primaryTypographyProps={{ fontSize: "0.94rem", fontWeight: childActive ? 700 : 500 }}
          />
          {locked ? (
            <LockOutlinedIcon sx={{ fontSize: 16, color: C.muted }} />
          ) : (
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                color: C.muted,
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 200ms",
              }}
            />
          )}
        </ListItemButton>
        <Collapse in={open && !locked} unmountOnExit>
          {entry.children.map((leaf) => renderLeaf(leaf, true, entry.feature))}
        </Collapse>
      </Box>
    );
  };

  const displayName = (user as any)?.fullName || (user as any)?.email || "Account";
  const noteDays = `${subscription?.daysLeft} day${subscription?.daysLeft === 1 ? "" : "s"}`;
  const trialNote =
    subscription?.status === "GRACE"
      ? `Payment due · locks in ${noteDays}`
      : subscription && (subscription.status === "TRIAL" || subscription.status === "EXPIRING")
        ? `${subscription.status === "TRIAL" ? "Trial" : "Plan"} · ${noteDays} left`
        : null;

  const nav = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#FFFFFF" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, pt: 2.5, pb: 2 }}>
        <Box
          component="img"
          src={logo}
          alt=""
          sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: C.tealTint, p: 0.5 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: C.ink }} noWrap>
            {clinicName}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: C.muted }} noWrap>
            {BRAND.name}
          </Typography>
        </Box>
      </Box>

      <Box component="nav" aria-label="Clinic" sx={{ flexGrow: 1, overflowY: "auto", px: 1.25 }}>
        {sections.map((section, index) => (
          <Box key={section.title ?? index} sx={{ mb: 1.5 }}>
            {section.title && (
              <Typography
                sx={{
                  px: 1.5,
                  pt: 1,
                  pb: 0.75,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: C.muted,
                }}
              >
                {section.title}
              </Typography>
            )}
            <List disablePadding>{section.entries.map(renderEntry)}</List>
          </Box>
        ))}
      </Box>

      {trialNote && (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => navigate(`${base}/subscription`)}
          onKeyDown={(e) => e.key === "Enter" && navigate(`${base}/subscription`)}
          sx={{
            mx: 1.5,
            mb: 1.5,
            p: 1.5,
            borderRadius: 2.5,
            bgcolor: C.tealTint,
            cursor: "pointer",
          }}
        >
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: C.teal }}>
            {trialNote}
          </Typography>
          <Typography sx={{ fontSize: "0.76rem", color: C.muted }}>See packages</Typography>
        </Box>
      )}

      <HelpRow color={C.muted} onOpen={() => setDrawerOpen(false)} />
      <Divider />
      <Box
        role="button"
        tabIndex={0}
        onClick={(e) => setUserMenu(e.currentTarget)}
        onKeyDown={(e) => e.key === "Enter" && setUserMenu(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { bgcolor: C.page },
        }}
      >
        <Avatar sx={{ width: 34, height: 34, bgcolor: C.teal, fontSize: "0.9rem" }}>
          {String(displayName).charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", color: C.ink }} noWrap>
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: "0.74rem", color: C.muted }} noWrap>
            {(user as any)?.role === "ADMIN" ? "Owner" : (user as any)?.role || ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const bottomTabs = [
    { label: "Today", icon: <TodayIcon />, to: base, exact: true },
    { label: "Schedule", icon: <CalendarMonthIcon />, to: `${base}/appointment` },
    { label: LABELS.clients, icon: <PeopleIcon />, to: `${base}/clients/clients` },
  ];
  const bottomValue =
    bottomTabs.findIndex((tab) => isActive(tab as NavLeaf)) >= 0
      ? bottomTabs.findIndex((tab) => isActive(tab as NavLeaf))
      : 3;

  return (
    <TourProvider base={base}>
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: C.page }}>
      {isDesktop ? (
        <Box
          sx={{
            width: NAV_WIDTH,
            flexShrink: 0,
            borderRight: `1px solid ${C.line}`,
            position: "sticky",
            top: 0,
            height: "100vh",
          }}
        >
          {nav}
        </Box>
      ) : (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: NAV_WIDTH + 16 } }}
        >
          {nav}
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <TopBar
          title={activeLeaf?.label ?? ""}
          businessId={businessId}
          showMenuButton={!isDesktop}
          compact={isPhone}
          onMenu={() => setDrawerOpen(true)}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // Edge to edge on every page: thin margins and no max width, so
            // tables, the schedule and the Today page use the whole screen.
            px: { xs: 1, sm: 1.25 },
            pt: { xs: 1, sm: 1.25 },
            // Room for the bottom tab bar on phones.
            pb: { xs: 11, sm: 2 },
            width: "100%",
            mx: "auto",
            boxSizing: "border-box",
            "& > *": { minWidth: 0 },
          }}
        >
          <SubscriptionBanner sx={{ mb: 2 }} />
          <PageIconContext.Provider value={activeLeaf?.icon ?? null}>
            <PageTransition>
              <Outlet />
            </PageTransition>
          </PageIconContext.Provider>
        </Box>
      </Box>

      {isPhone && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.appBar,
            borderTop: `1px solid ${C.line}`,
            pb: "env(safe-area-inset-bottom)",
          }}
        >
          <BottomNavigation
            showLabels
            value={bottomValue}
            onChange={(_, index) => {
              if (index === 3) setDrawerOpen(true);
              else navigate(bottomTabs[index].to);
            }}
            sx={{
              height: 64,
              "& .MuiBottomNavigationAction-root": { minWidth: 0, color: C.muted },
              "& .Mui-selected": { color: C.teal },
            }}
          >
            {bottomTabs.map((tab) => (
              <BottomNavigationAction key={tab.to} label={tab.label} icon={tab.icon} />
            ))}
            <BottomNavigationAction label="More" icon={<MoreHorizIcon />} />
          </BottomNavigation>
        </Paper>
      )}

      <Menu anchorEl={userMenu} open={!!userMenu} onClose={() => setUserMenu(null)}>
        <MenuItem
          onClick={() => {
            setUserMenu(null);
            navigate("/profile");
          }}
        >
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            setUserMenu(null);
            navigate("/home?all=1");
          }}
        >
          All {LABELS.venues.toLowerCase()}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setUserMenu(null);
            navigate(`${base}/subscription`);
          }}
        >
          Subscription
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setUserMenu(null);
            logout();
          }}
        >
          Log out
        </MenuItem>
      </Menu>
    </Box>
    </TourProvider>
  );
};

/* ------------------------------------------------------------------ */
/* Top bar: page title, find-a-patient, new appointment                */
/* ------------------------------------------------------------------ */

const TopBar: React.FC<{
  title: string;
  businessId?: string;
  showMenuButton: boolean;
  compact: boolean;
  onMenu: () => void;
}> = ({ title, businessId, showMenuButton, compact, onMenu }) => {
  const navigate = useNavigate();
  const base = `/dashboard/${businessId}`;
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ClientSelectItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!businessId || query.trim().length < 2) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      getClientsSelect({ businessId, search: query, limit: 8 })
        .then((res) => !cancelled && setOptions(res?.content ?? []))
        .catch(() => !cancelled && setOptions([]))
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, businessId]);

  const search = (
    <Autocomplete<ClientSelectItem, false, false, false>
      size="small"
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.name || option.label || ""}
      noOptionsText={query.trim().length < 2 ? "Type at least 2 letters" : `No ${LABELS.clients.toLowerCase()} found`}
      onInputChange={(_, value) => setQuery(value)}
      onChange={(_, option) => {
        if (option) {
          navigate(`${base}/clients/details/${option.id}`);
          setQuery("");
          setSearchOpen(false);
        }
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id} sx={{ display: "block !important" }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }}>{option.name}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: C.muted }}>
            {[option.country_code && `+${String(option.country_code).replace("+", "")}`, option.phone || option.mobile]
              .filter(Boolean)
              .join(" ")}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={compact && searchOpen}
          placeholder={`Find a ${LABELS.client.toLowerCase()} by name or phone`}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: C.muted }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      sx={{ width: compact ? "100%" : { sm: 260, md: 280, lg: 380 } }}
    />
  );

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        bgcolor: "rgba(245, 249, 250, 0.92)",
        backdropFilter: "blur(8px)",
        borderBottom: `1px solid ${C.line}`,
        px: { xs: 1, sm: 3 },
        py: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {showMenuButton && (
          <IconButton aria-label="Open menu" onClick={onMenu} sx={{ color: C.ink }}>
            <MenuIcon />
          </IconButton>
        )}
        {!(compact && searchOpen) && (
          <Typography
            component="h1"
            sx={{ fontWeight: 800, fontSize: { xs: "1.1rem", sm: "1.25rem" }, color: C.ink, flexGrow: 1, minWidth: 0 }}
            noWrap
          >
            {title}
          </Typography>
        )}
        {compact ? (
          searchOpen ? (
            <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 1 }}>
              {search}
              <Button onClick={() => setSearchOpen(false)} sx={{ minWidth: 0 }}>
                Cancel
              </Button>
            </Box>
          ) : (
            <Tooltip title={`Find a ${LABELS.client.toLowerCase()}`}>
              <IconButton aria-label={`Find a ${LABELS.client.toLowerCase()}`} onClick={() => setSearchOpen(true)}>
                <SearchIcon />
              </IconButton>
            </Tooltip>
          )
        ) : (
          search
        )}
        {!(compact && searchOpen) &&
          (compact ? (
            <IconButton
              aria-label="New appointment"
              data-tour="new-appointment"
              onClick={() => navigate(`${base}/appointment?new=1`)}
              sx={{ bgcolor: C.teal, color: "#fff", "&:hover": { bgcolor: "#0B5F75" } }}
            >
              <AddIcon />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`${base}/appointment?new=1`)}
              sx={{ whiteSpace: "nowrap" }}
              data-tour="new-appointment"
            >
              New appointment
            </Button>
          ))}
      </Box>
    </Box>
  );
};

export default ClinicLayout;
